import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import { Media } from "./Media";
import { Button } from "./ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./ui/navigation-menu";
import { MobileMenu } from "./mobile/MobileMenu";
import { trackDemoClick } from "@/lib/telemetry";
import { DEMO_URL, NAV_GROUPS, PLATFORM_URL, isExternalHref, type NavItem } from "@/lib/navigation";
import logoAsset from "@/assets/datasyteAI-logo-black.png.asset.json";

const logo = logoAsset.url;

const MegaLink = ({ item }: { item: NavItem }) => {
  const external = isExternalHref(item.href);
  const className =
    "flex h-full w-full min-w-0 select-none flex-col gap-1 rounded-lg px-3 py-2.5 no-underline outline-none transition-colors hover:bg-primary/5 focus-visible:bg-primary/5 focus-visible:ring-2 focus-visible:ring-ring";
  const body = (
    <>
      <span className="flex min-w-0 items-start gap-1.5 text-sm font-semibold leading-snug tracking-tight text-foreground">
        <span className="min-w-0 break-words">{item.label}</span>
        {external && <ArrowUpRight size={13} className="mt-0.5 shrink-0" aria-hidden="true" />}
      </span>
      {item.description && (
        <span className="block break-words text-xs leading-relaxed text-muted-foreground">{item.description}</span>
      )}
    </>
  );


  return (
    <li className="flex min-w-0">
      <NavigationMenuLink asChild>
        {external ? (
          <a href={item.href} target="_blank" rel="noopener noreferrer" className={className}>
            {body}
          </a>
        ) : (
          <Link to={item.href} className={className}>
            {body}
          </Link>
        )}
      </NavigationMenuLink>
    </li>
  );
};

export const Header = () => {
  const [compact, setCompact] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setCompact(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-md">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        Skip to content
      </a>
      <div
        className={`container mx-auto px-6 transition-all duration-200 ${
          compact ? "py-2" : "py-4"
        }`}
      >
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 rounded-md" aria-label="datasyteAI home">
            <Media
              src={logo}
              alt="datasyteAI™ by PathFree Technologies logo"
              className={`w-auto object-contain transition-all duration-200 ${
                compact ? "h-9" : "h-12"
              }`}
              sizes="230px"
              eager
            />
            <span
              className={`hidden xl:block border-l border-border pl-3 text-[11px] leading-tight text-muted-foreground transition-opacity ${
                compact ? "opacity-0 xl:hidden" : "opacity-100"
              }`}
            >
              PathFree Technologies
              <br />
              datasyteAI™ · AiMediQ™
            </span>
          </Link>

          <div className="hidden lg:flex flex-1 justify-center">
            <NavigationMenu>
              <NavigationMenuList className="gap-0.5">
                {NAV_GROUPS.map((group) => (
                  <NavigationMenuItem key={group.id}>
                    <NavigationMenuTrigger className="h-9 whitespace-nowrap bg-transparent px-2.5 text-[13px] font-medium tracking-tight xl:px-3 xl:text-sm">
                      {group.label}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[min(90vw,600px)] p-4">
                        <p className="px-3 pb-2 text-xs leading-relaxed text-muted-foreground">{group.blurb}</p>
                        <ul className="grid auto-rows-fr gap-x-3 gap-y-1 md:grid-cols-2">
                          {group.items.map((item) => (
                            <MegaLink key={item.href} item={item} />
                          ))}
                        </ul>
                      </div>
                    </NavigationMenuContent>

                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="hidden lg:flex items-center gap-2">
            <Button asChild variant="ghost" size={compact ? "sm" : "default"}>
              <Link to={PLATFORM_URL}>Explore the Platform</Link>
            </Button>
            {!isHome && (
              <Button asChild size={compact ? "sm" : "default"}>
                <a href={DEMO_URL} onClick={() => trackDemoClick("header_desktop")}>
                  Request a Demo
                </a>
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            {!isHome && (
              <Button asChild size="sm">
                <a href={DEMO_URL} onClick={() => trackDemoClick("header_mobile_cta")}>
                  Request a Demo
                </a>
              </Button>
            )}
            <MobileMenu />
          </div>
        </div>
      </div>
    </header>
  );
};
